import { query } from 'bitecs';
import { Config } from '@src/core/Config';
import { BossImpactKind, GameEvent } from '@src/core/EventQueue';
import {
  kill,
  NO_ENTITY,
  playClip,
  randomPrizeKind,
  spawnBat,
  spawnCinder,
  spawnFx,
  spawnGem,
  spawnGolem,
  spawnHeart,
  spawnHexer,
  spawnPrize,
  spawnProjectile,
  spawnSkull,
  spawnSlime,
  spawnWeaponPickup,
  spawnWraith,
  type ProjectileSpec,
} from '@src/ecs/archetypes';
import {
  Anim,
  BOSS_ORBS,
  BossKind,
  BossTag,
  ClipId,
  Damage,
  Dying,
  Enemy,
  EnemyKind,
  FxTag,
  Health,
  Lifetime,
  Player,
  Position,
  PrevPosition,
  Projectile,
  Radius,
  Sprite,
  SpriteId,
  Team,
  TeamTag,
  Velocity,
} from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { emitParticles, ParticleKind, requestDecal, RoomPhase, type GameContext } from '@src/ecs/world';
import { Anims } from '@src/game/AnimDefs';
import { emitBossKillGore } from '@src/game/DeathFx';
import { BOUNDS } from '@src/game/arena/RoomLayout';
import { BossBody, bossDef, BossEntrance, BossMotion, BossPattern, bossTuning, type BossDef, type BossTuning } from '@src/game/BossDefs';
import { isWallEnemy } from '@src/game/WallEnemies';
import { DROP_POOL, WeaponId } from '@src/game/weapons/WeaponDefs';

/** BossTag.phase values. */
export const BOSS_ARRIVE = 0;
export const BOSS_FIGHT = 1;

const DEG = Math.PI / 180;
/** Marker on Projectile.weapon so the FX path stays on the plain bolt branch. */
const SPELL_WEAPON = WeaponId.HydraMortar;
const WISP_TURN_RATE = 2.1;
const ORBIT_SECONDS = 2.1;
const CROWN_SPIN_SECONDS = 1.5;
const CROWN_LAUNCH_GAP = 0.13;
const BEAM_SWEEP_SECONDS = 1.45;
const BEAM_SWEEP_DEG = 78;
const BEAM_SHOT_GAP = 0.045;
const CRESCENDO_BURST_GAP = 0.2;
/** Stomp ring: how far it reaches and how long it takes to get there. */
const STOMP_RADIUS = 6.2;
const STOMP_SECONDS = 1.05;
/** Charge speeds (wu/s) and the dive's fixed length in seconds. */
const CHARGE_SPEED = 13;
const DIVE_SPEED = 17;
const DIVE_SECONDS = 0.8;
const HOP_INTERVAL = 0.95;
const HOP_SECONDS = 0.32;
const FLYER_ORBIT_RADIUS = 5.2;
/** Small fry the summoners will keep alive at most. */
const SUMMON_CAP = 7;
/** A boss with no target hovers around the middle. */
const IDLE_X = 0;
const IDLE_Y = 0;

interface PatternScript {
  /** Seconds the boss glows before the first release. */
  telegraph: number;
  /** Seconds between releases. */
  gap: number;
}

const SCRIPTS: Readonly<Record<BossPattern, PatternScript>> = {
  [BossPattern.Radial]: { telegraph: 0.7, gap: 0.42 },
  [BossPattern.Aimed]: { telegraph: 0.55, gap: 0.28 },
  [BossPattern.Orbit]: { telegraph: 0.45, gap: ORBIT_SECONDS },
  [BossPattern.RingGap]: { telegraph: 0.85, gap: 1.1 },
  [BossPattern.Wisps]: { telegraph: 0.5, gap: 0.35 },
  [BossPattern.Beam]: { telegraph: 1.05, gap: BEAM_SHOT_GAP },
  [BossPattern.Stomp]: { telegraph: 0.9, gap: 0.75 },
  [BossPattern.Charge]: { telegraph: 0.85, gap: 0.1 },
  [BossPattern.Rubble]: { telegraph: 0.6, gap: 0.5 },
  [BossPattern.Crown]: { telegraph: 0.5, gap: CROWN_SPIN_SECONDS },
  [BossPattern.Summon]: { telegraph: 0.75, gap: 0.5 },
  [BossPattern.Acid]: { telegraph: 0.6, gap: 0.45 },
  [BossPattern.Screech]: { telegraph: 0.7, gap: 0.9 },
  [BossPattern.COUNT]: { telegraph: 0.5, gap: 0.5 },
};

/**
 * Boss brain for archmages and room champions. Entrance ritual (invulnerable; blink in,
 * crash down, rise from the floor or swoop over the wall depending on the boss), then a
 * pattern loop: telegraph, release, rest, locomotion between casts. The unlocked spell
 * set, projectile speed and cadence come from bossTuning(kind, floor, room).
 * Death is a timed crescendo of bursts before the room is allowed to clear.
 */
export class BossSystem implements GameSystem {
  private ctx!: GameContext;
  private tuning: BossTuning = bossTuning(0, 1, 1);
  private tunedEid = -1;
  private tunedKind = -1;
  private tunedRoom = -1;
  private readonly shot: ProjectileSpec = {
    owner: -1,
    weapon: SPELL_WEAPON,
    sprite: SpriteId.BossBolt,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    radius: 0.24,
    life: 3,
    damage: 10,
    spawnerMult: 0,
    knockback: 4,
    pierce: 0,
    tint: 0xffffff,
    scale: 1,
    grow: 0,
    team: Team.Enemy,
  };

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  update(dt: number): void {
    const ctx = this.ctx;
    const bosses = query(ctx.world, [BossTag, Position, Velocity, Sprite, Health]);
    const players = query(ctx.world, [Player, Position]);
    for (let i = 0; i < bosses.length; i++) {
      const eid = bosses[i];
      this.tune(eid);
      if (Dying.flag[eid]) {
        this.crescendo(eid, dt);
        continue;
      }
      BossTag.phaseTime[eid] += dt;
      if (Enemy.stagger[eid] > 0) Enemy.stagger[eid] = Math.max(0, Enemy.stagger[eid] - dt);
      if (BossTag.phase[eid] === BOSS_ARRIVE) this.arrive(eid, dt);
      else this.fight(eid, players, dt);
    }
    this.steerWisps(players, dt);
    this.burstAcid(dt);
  }

  /** Tuning is per boss (kind + room); cached so the common single-boss case is free. */
  private tune(eid: number): void {
    const kind = BossTag.kind[eid];
    const room = BossTag.room[eid] || this.ctx.room.roomIndex;
    if (eid === this.tunedEid && kind === this.tunedKind && room === this.tunedRoom) return;
    this.tunedEid = eid;
    this.tunedKind = kind;
    this.tunedRoom = room;
    this.tuning = bossTuning(kind, this.ctx.floor.floor, room, BossTag.hpMult[eid] || 1);
  }

  // ---------------------------------------------------------------- entrance

  private arrive(eid: number, dt: number): void {
    const def = bossDef(BossTag.kind[eid]);
    const T = this.tuning.arriveSeconds;
    const t = BossTag.phaseTime[eid];
    const u = Math.min(1, t / T);
    const prevU = Math.max(0, (t - dt) / T);
    Velocity.x[eid] = 0;
    Velocity.y[eid] = 0;
    if (t - dt <= 0) this.arriveStart(eid, def);

    switch (def.entrance) {
      case BossEntrance.Crash:
        this.arriveCrash(eid, def, u, prevU, dt);
        break;
      case BossEntrance.Rise:
        this.arriveRise(eid, def, u, prevU, dt);
        break;
      case BossEntrance.Swoop:
        this.arriveSwoop(eid, def, u, prevU, dt);
        break;
      case BossEntrance.Blink:
      default:
        this.arriveBlink(eid, def, u, prevU, dt);
        break;
    }
    if (u < 1) return;

    // The fight is on.
    BossTag.phase[eid] = BOSS_FIGHT;
    BossTag.phaseTime[eid] = 0;
    BossTag.rest[eid] = def.archmage ? 0.9 : 1.1;
    BossTag.blinkCooldown[eid] = def.motion === BossMotion.Hop ? 0.4 : Config.boss.blinkCooldown * 0.5;
    Sprite.alpha[eid] = 1;
    Sprite.joltY[eid] = 0;
    BossTag.lift[eid] = def.lift;
    Health.invuln[eid] = 0.05;
    playClip(eid, ClipId.Idle, true);
    Anim.speed[eid] = 1;
  }

  /** First frame of every entrance: the rune circle under the landing point flares. */
  private arriveStart(eid: number, def: BossDef): void {
    const ctx = this.ctx;
    const x = Position.x[eid];
    const y = Position.y[eid];
    spawnFx(ctx, SpriteId.Shockwave, x, y, 0, 2.4, def.glowTint, 0.55);
    emitParticles(ctx.fx, ParticleKind.PortalBurst, x, y, 0, 0, 1.6, def.glowTint);
    ctx.fx.rumble = 1;
    ctx.fx.shakeY += 0.5;
    Sprite.alpha[eid] = 0;
    BossTag.lift[eid] = def.lift;
    if (def.entrance === BossEntrance.Swoop) {
      // Start pressed against the wall farthest from the wizard; the arc carries it in.
      const fromLeft = x <= 0;
      const sx = fromLeft ? BOUNDS.minX + Radius.r[eid] : BOUNDS.maxX - Radius.r[eid];
      BossTag.targetX[eid] = x;
      BossTag.targetY[eid] = y;
      Position.x[eid] = sx;
      Position.y[eid] = y;
      PrevPosition.x[eid] = sx;
      PrevPosition.y[eid] = y;
      Sprite.face[eid] = fromLeft ? 1 : -1;
    }
  }

  private landed(eid: number, def: BossDef, heavy: number): void {
    const ctx = this.ctx;
    const x = Position.x[eid];
    const y = Position.y[eid];
    Sprite.alpha[eid] = 1;
    Sprite.flash[eid] = 1;
    Sprite.pop[eid] = 0.5 * heavy;
    Sprite.squash[eid] = 0.45 * heavy;
    spawnFx(ctx, SpriteId.Explosion, x, y, ctx.rng.next() * Math.PI * 2, 2.6 + heavy, def.glowTint, 0.85);
    spawnFx(ctx, SpriteId.Shockwave, x, y, 0, 3 + heavy * 2, def.glowTint, 0.55);
    emitParticles(ctx.fx, ParticleKind.GateDust, x, y - 0.6, 0, -1, 1.2 + heavy * 0.6);
    emitParticles(ctx.fx, ParticleKind.Embers, x, y, 0, 1, 1.4, def.boltTint);
    ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 90 + heavy * 60);
    ctx.fx.flash = Math.max(ctx.fx.flash, 0.35 + heavy * 0.25);
    ctx.fx.shakeX += 0.5 + heavy * 0.5;
    ctx.fx.shakeY += 0.7 + heavy * 0.7;
    ctx.fx.rumble = 1;
    ctx.events.push(GameEvent.BossLanded, eid, BossTag.kind[eid], x, y);
  }

  /** Archmage / Skull King: condenses out of rune motes, with a second pulse halfway. */
  private arriveBlink(eid: number, def: BossDef, u: number, prevU: number, dt: number): void {
    const ctx = this.ctx;
    const x = Position.x[eid];
    const y = Position.y[eid];
    Sprite.alpha[eid] = Math.max(0, Math.min(1, (u - 0.25) / 0.6));
    Sprite.squash[eid] = -0.35 * (1 - u);
    if (ctx.rng.chance(dt * 30 * (1 - u * 0.5))) {
      const a = ctx.rng.next() * Math.PI * 2;
      const r = 1.2 + ctx.rng.next() * 1.8;
      emitParticles(ctx.fx, ParticleKind.PortalMotes, x + Math.cos(a) * r, y + Math.sin(a) * r, -Math.cos(a), -Math.sin(a), 0.9, def.glowTint);
    }
    if (u >= 0.55 && prevU < 0.55) {
      spawnFx(ctx, SpriteId.Shockwave, x, y, 0, 3.6, def.glowTint, 0.55);
      ctx.fx.shakeX += (ctx.rng.next() - 0.5) * 0.6;
    }
    if (u >= 1 && prevU < 1) this.landed(eid, def, 1);
  }

  /** Warlord: a shadow grows on the rune, then the body drops out of the dark and craters. */
  private arriveCrash(eid: number, def: BossDef, u: number, prevU: number, dt: number): void {
    const ctx = this.ctx;
    const x = Position.x[eid];
    const y = Position.y[eid];
    const dropAt = 0.42;
    const landAt = 0.82;
    if (u < dropAt) {
      Sprite.alpha[eid] = 0;
      BossTag.lift[eid] = 9;
      // Rubble sifts down ahead of it.
      if (ctx.rng.chance(dt * 14)) emitParticles(ctx.fx, ParticleKind.GateDust, x + (ctx.rng.next() - 0.5) * 2.5, y + 1.5, 0, -1, 0.5);
      return;
    }
    if (u < landAt) {
      const f = (u - dropAt) / (landAt - dropAt);
      Sprite.alpha[eid] = 1;
      // Gravity: quadratic fall from well above the frame.
      BossTag.lift[eid] = 9 * (1 - f * f);
      Sprite.squash[eid] = -0.25;
      if (ctx.rng.chance(dt * 20)) emitParticles(ctx.fx, ParticleKind.Embers, x, y + BossTag.lift[eid], 0, 1, 0.5, def.glowTint);
      return;
    }
    if (prevU < landAt) {
      BossTag.lift[eid] = 0;
      this.landed(eid, def, 2);
      requestDecal(ctx.fx, x, y, def.stainTint, 2.6);
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 + ctx.rng.next() * 0.4;
        this.bolt(eid, def, x + Math.cos(a) * 1.2, y + Math.sin(a) * 1.2, a, 5.5 + ctx.rng.next() * 3, SpriteId.BossRubble, 0, 0.9, 0.22, 0.9);
      }
      ctx.events.push(GameEvent.BossImpact, BossImpactKind.Land, BossTag.kind[eid], x, y);
    }
    const f = (u - landAt) / (1 - landAt);
    Sprite.squash[eid] = 0.45 * (1 - f);
    BossTag.lift[eid] = 0;
  }

  /** Colossus / Sentinel: flagstones heave, then the body pushes up through the floor. */
  private arriveRise(eid: number, def: BossDef, u: number, prevU: number, dt: number): void {
    const ctx = this.ctx;
    const x = Position.x[eid];
    const y = Position.y[eid];
    const showAt = 0.3;
    const upAt = 0.86;
    ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.6 + u * 0.4);
    if (ctx.rng.chance(dt * 18)) {
      const a = ctx.rng.next() * Math.PI * 2;
      const r = ctx.rng.next() * Radius.r[eid] * 1.4;
      emitParticles(ctx.fx, def.body === BossBody.Goo ? ParticleKind.ImpGoo : ParticleKind.GateDust, x + Math.cos(a) * r, y + Math.sin(a) * r - 0.4, 0, 1, 0.6, def.body === BossBody.Goo ? def.orbTint : 0xffffff);
    }
    if (u < showAt) {
      Sprite.alpha[eid] = 0;
      ctx.fx.shakeY += dt * 1.2;
      return;
    }
    if (u < upAt) {
      const f = (u - showAt) / (upAt - showAt);
      Sprite.alpha[eid] = 1;
      // Flattened into the floor, stretching upward as it rises.
      Sprite.squash[eid] = -0.95 * (1 - f) + 0.15 * f;
      BossTag.lift[eid] = -Radius.r[eid] * 1.2 * (1 - f);
      ctx.fx.shakeY += dt * 0.8;
      return;
    }
    if (prevU < upAt) {
      BossTag.lift[eid] = 0;
      this.landed(eid, def, 1.4);
    }
    const f = (u - upAt) / (1 - upAt);
    Sprite.squash[eid] = 0.35 * (1 - f);
    BossTag.lift[eid] = 0;
  }

  /** Matriarch: swoops in over the far wall on a high arc and slams down on the rune. */
  private arriveSwoop(eid: number, def: BossDef, u: number, prevU: number, dt: number): void {
    const ctx = this.ctx;
    const showAt = 0.28;
    const landAt = 0.84;
    const sx = Position.x[eid];
    const sy = Position.y[eid];
    if (u < showAt) {
      Sprite.alpha[eid] = 0;
      BossTag.lift[eid] = 6;
      return;
    }
    if (u < landAt) {
      const f = (u - showAt) / (landAt - showAt);
      const e = f * f * (3 - 2 * f);
      Sprite.alpha[eid] = 1;
      const tx = BossTag.targetX[eid];
      const ty = BossTag.targetY[eid];
      // Start point was parked at the wall on the first frame; glide toward the rune.
      if (prevU < showAt) {
        BossTag.lockX[eid] = sx;
        BossTag.lockY[eid] = sy;
      }
      const nx = BossTag.lockX[eid] + (tx - BossTag.lockX[eid]) * e;
      const ny = BossTag.lockY[eid] + (ty - BossTag.lockY[eid]) * e;
      Position.x[eid] = nx;
      Position.y[eid] = ny;
      BossTag.lift[eid] = def.lift + 6 * (1 - f) * (1 - f) + Math.sin(f * Math.PI) * 1.5;
      Sprite.squash[eid] = -0.2;
      BossTag.bob[eid] += dt * 14;
      if (ctx.rng.chance(dt * 16)) emitParticles(ctx.fx, ParticleKind.GateDust, nx, ny, 0, -1, 0.4);
      return;
    }
    if (prevU < landAt) {
      Position.x[eid] = BossTag.targetX[eid];
      Position.y[eid] = BossTag.targetY[eid];
      PrevPosition.x[eid] = Position.x[eid];
      PrevPosition.y[eid] = Position.y[eid];
      BossTag.lockX[eid] = 0;
      BossTag.lockY[eid] = 0;
      BossTag.lift[eid] = def.lift;
      this.landed(eid, def, 1.2);
      ctx.events.push(GameEvent.BossImpact, BossImpactKind.Land, BossTag.kind[eid], Position.x[eid], Position.y[eid]);
    }
    const f = (u - landAt) / (1 - landAt);
    Sprite.squash[eid] = 0.35 * (1 - f);
  }

  // ------------------------------------------------------------------- fight

  private fight(eid: number, players: ArrayLike<number>, dt: number): void {
    const ctx = this.ctx;
    const c = Config.boss;
    const def = bossDef(BossTag.kind[eid]);
    BossTag.bob[eid] += dt * (def.motion === BossMotion.Flyer ? 9 : 2.4);
    BossTag.blinkCooldown[eid] -= dt;
    if (def.lift > 0) {
      BossTag.lift[eid] = def.lift + Math.sin(BossTag.bob[eid]) * (def.motion === BossMotion.Flyer ? 0.12 : 0.18);
    }

    // Nearest living wizard.
    let tx = IDLE_X;
    let ty = IDLE_Y;
    let best = Infinity;
    for (let p = 0; p < players.length; p++) {
      const pid = players[p];
      if (Player.dead[pid]) continue;
      const dx = Position.x[pid] - Position.x[eid];
      const dy = Position.y[pid] - Position.y[eid];
      const d2 = dx * dx + dy * dy;
      if (d2 >= best) continue;
      best = d2;
      tx = Position.x[pid];
      ty = Position.y[pid];
    }
    const hasTarget = best !== Infinity;
    const dist = hasTarget ? Math.sqrt(best) : Infinity;
    const charging = BossTag.pattern[eid] === BossPattern.Charge && BossTag.step[eid] > 0 && BossTag.rest[eid] <= 0;
    if (hasTarget && !charging && Math.abs(tx - Position.x[eid]) > 0.3) Sprite.face[eid] = tx < Position.x[eid] ? -1 : 1;

    // Enrage at half health: one line, a flash, shorter rests and a colour shift from here on.
    if (!BossTag.halfCalled[eid] && Health.hp[eid] <= Health.max[eid] * c.enrageBelow) {
      BossTag.halfCalled[eid] = 1;
      BossTag.enraged[eid] = 1;
      Sprite.flash[eid] = 1;
      Sprite.pop[eid] = 0.5;
      spawnFx(ctx, SpriteId.Shockwave, Position.x[eid], Position.y[eid], 0, 3, def.glowTint, 0.55);
      emitParticles(ctx.fx, ParticleKind.PortalBurst, Position.x[eid], Position.y[eid], 0, 0, 1.2, def.boltTint);
      ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 80);
      ctx.fx.shakeX += 0.4;
      ctx.events.push(GameEvent.BossHalf, eid, BossTag.kind[eid], Position.x[eid], Position.y[eid]);
    }
    // Colossus: sheds a ring of slimes at half and quarter health and shrinks each time.
    if (def.kind === BossKind.Colossus) this.maybeSplit(eid, def);

    const resting = BossTag.rest[eid] > 0;
    if (resting) {
      this.locomote(eid, def, hasTarget, tx, ty, dist, dt);
      BossTag.rest[eid] -= dt;
      if (BossTag.rest[eid] <= 0) this.startPattern(eid, tx, ty);
      return;
    }

    // Casting: hold position (a slow drift keeps it alive) and run the script.
    if (!charging) {
      Velocity.x[eid] *= 0.85;
      Velocity.y[eid] *= 0.85;
    }
    this.runPattern(eid, tx, ty, dt);
  }

  // -------------------------------------------------------------- locomotion

  private locomote(eid: number, def: BossDef, hasTarget: boolean, tx: number, ty: number, dist: number, dt: number): void {
    const c = Config.boss;
    const speedMult = BossTag.enraged[eid] ? 1.25 : 1;
    switch (def.motion) {
      case BossMotion.Walk: {
        const x = Position.x[eid];
        const y = Position.y[eid];
        const dx = tx - x;
        const dy = ty - y;
        const d = Math.hypot(dx, dy) || 1;
        const want = hasTarget && d > 2.2 ? def.speed * speedMult : 0;
        const k = 1 - Math.exp(-3 * dt);
        Velocity.x[eid] += ((dx / d) * want - Velocity.x[eid]) * k;
        Velocity.y[eid] += ((dy / d) * want - Velocity.y[eid]) * k;
        if (want > 0) {
          playClip(eid, ClipId.Run);
          Anim.speed[eid] = 1;
          // Each footfall shakes loose a little dust.
          if (this.ctx.rng.chance(dt * 3.5)) {
            emitParticles(this.ctx.fx, ParticleKind.GateDust, x, y - 0.9, 0, 1, 0.35);
            this.ctx.fx.shakeY += 0.06;
            this.ctx.fx.rumble = Math.max(this.ctx.fx.rumble, 0.25);
          }
        } else {
          playClip(eid, ClipId.Idle);
        }
        break;
      }
      case BossMotion.Hop: {
        // blinkCooldown doubles as the hop timer for grounded bosses.
        const t = BossTag.blinkCooldown[eid];
        if (t <= 0 && hasTarget && dist > 1.8) {
          const dx = tx - Position.x[eid];
          const dy = ty - Position.y[eid];
          const d = Math.hypot(dx, dy) || 1;
          const speed = def.speed * speedMult * 2.2;
          Velocity.x[eid] = (dx / d) * speed;
          Velocity.y[eid] = (dy / d) * speed;
          BossTag.blinkCooldown[eid] = HOP_INTERVAL / speedMult;
          Sprite.squash[eid] = -0.35;
          Sprite.pop[eid] = 0.2;
          emitParticles(this.ctx.fx, ParticleKind.ImpGoo, Position.x[eid], Position.y[eid] - 0.5, 0, 1, 0.5, def.orbTint);
        } else if (t > 0 && t < HOP_INTERVAL / speedMult - HOP_SECONDS) {
          // Landed: skid to a stop, jiggle.
          Velocity.x[eid] *= Math.exp(-10 * dt);
          Velocity.y[eid] *= Math.exp(-10 * dt);
          const landAt = HOP_INTERVAL / speedMult - HOP_SECONDS;
          if (t + dt >= landAt) {
            Sprite.squash[eid] = 0.45;
            emitParticles(this.ctx.fx, ParticleKind.ImpGoo, Position.x[eid], Position.y[eid] - 0.5, 0, 1, 0.7, def.orbTint);
            this.ctx.fx.shakeY += 0.2;
            this.ctx.fx.rumble = Math.max(this.ctx.fx.rumble, 0.4);
          }
        }
        BossTag.lift[eid] = Math.max(0, Math.sin(Math.min(1, Math.max(0, (HOP_INTERVAL / speedMult - t) / HOP_SECONDS)) * Math.PI)) * 0.8;
        break;
      }
      case BossMotion.Flyer: {
        // Circle the wizard at a fixed radius; the spiral direction flips per cast.
        const x = Position.x[eid];
        const y = Position.y[eid];
        const cx = hasTarget ? tx : IDLE_X;
        const cy = hasTarget ? ty : IDLE_Y;
        const dx = x - cx;
        const dy = y - cy;
        const d = Math.hypot(dx, dy) || 1;
        const side = BossTag.casts[eid] % 2 === 0 ? 1 : -1;
        const tangX = (-dy / d) * side;
        const tangY = (dx / d) * side;
        const radial = (FLYER_ORBIT_RADIUS - d) / FLYER_ORBIT_RADIUS;
        const speed = def.speed * speedMult;
        const wantX = (tangX + (dx / d) * radial * 1.6) * speed;
        const wantY = (tangY + (dy / d) * radial * 1.6) * speed;
        const k = 1 - Math.exp(-5 * dt);
        Velocity.x[eid] += (wantX - Velocity.x[eid]) * k;
        Velocity.y[eid] += (wantY - Velocity.y[eid]) * k;
        // Bank into the turn.
        Sprite.rot[eid] = -Velocity.x[eid] * 0.03;
        break;
      }
      case BossMotion.Static:
        Velocity.x[eid] = 0;
        Velocity.y[eid] = 0;
        break;
      case BossMotion.Hover:
      default: {
        // Crowded or overdue: blink to a fresh rune point before the next spell.
        const crowded = hasTarget && dist < c.blinkRange;
        const overdue = BossTag.casts[eid] > 0 && BossTag.casts[eid] % 3 === 0 && BossTag.blinkCooldown[eid] <= -1;
        if ((crowded && BossTag.blinkCooldown[eid] <= 0) || overdue) this.blink(eid, tx, ty);
        this.hover(eid, hasTarget, tx, ty, dist, dt);
        break;
      }
    }
  }

  private hover(eid: number, hasTarget: boolean, tx: number, ty: number, dist: number, dt: number): void {
    const c = Config.boss;
    const x = Position.x[eid];
    const y = Position.y[eid];
    let dx = BossTag.targetX[eid] - x;
    let dy = BossTag.targetY[eid] - y;
    // Keep the standoff: back away from a wizard that closes in, lean in when far.
    if (hasTarget && dist > 0.01) {
      const away = (c.standoff - dist) / c.standoff;
      if (away > 0) {
        dx += ((x - tx) / dist) * away * 3;
        dy += ((y - ty) / dist) * away * 3;
      }
    }
    const len = Math.hypot(dx, dy);
    const speed = len > 0.4 ? c.hoverSpeed : 0;
    const desiredX = len > 0.01 ? (dx / len) * speed : 0;
    const desiredY = len > 0.01 ? (dy / len) * speed : 0;
    const k = 1 - Math.exp(-4 * dt);
    Velocity.x[eid] += (desiredX - Velocity.x[eid]) * k;
    Velocity.y[eid] += (desiredY - Velocity.y[eid]) * k;
  }

  /** Teleport to the rune point farthest from the wizard (with a little variety). */
  private blink(eid: number, tx: number, ty: number): void {
    const ctx = this.ctx;
    const c = Config.boss;
    const def = bossDef(BossTag.kind[eid]);
    const x = Position.x[eid];
    const y = Position.y[eid];
    const anchors = c.anchors;
    // Rank anchors by distance from the wizard, skip the one we are on, pick from the top three.
    const order: number[] = [];
    for (let i = 0; i < anchors.length; i++) {
      const ax = anchors[i][0];
      const ay = anchors[i][1];
      if (Math.hypot(ax - x, ay - y) < 1.5) continue;
      order.push(i);
    }
    order.sort((a, b) => Math.hypot(anchors[b][0] - tx, anchors[b][1] - ty) - Math.hypot(anchors[a][0] - tx, anchors[a][1] - ty));
    const pick = order[ctx.rng.int(0, Math.min(2, order.length - 1))] ?? 0;
    const nx = anchors[pick][0];
    const ny = anchors[pick][1];

    spawnFx(ctx, SpriteId.Smoke, x, y, ctx.rng.next() * Math.PI * 2, 2.6, def.glowTint, 0.5);
    emitParticles(ctx.fx, ParticleKind.PortalMotes, x, y, 0, 1, 1.4, def.glowTint);
    if (def.kind === BossKind.SkullKing) {
      // Afterimages along the jump so the eye can follow the king.
      for (let k = 1; k <= 3; k++) {
        const f = k / 4;
        const fx = spawnFx(ctx, SpriteId.BossSkull, x + (nx - x) * f, y + (ny - y) * f, 0, 1.6, def.glowTint, 0.6);
        if (fx !== NO_ENTITY) {
          Lifetime.t[fx] = 0.25 + f * 0.15;
          Sprite.alpha[fx] = 0.5;
        }
      }
    }
    Position.x[eid] = nx;
    Position.y[eid] = ny;
    PrevPosition.x[eid] = nx;
    PrevPosition.y[eid] = ny;
    Velocity.x[eid] = 0;
    Velocity.y[eid] = 0;
    BossTag.targetX[eid] = nx;
    BossTag.targetY[eid] = ny;
    BossTag.blinkCooldown[eid] = c.blinkCooldown;
    Sprite.pop[eid] = 0.7;
    Sprite.flash[eid] = 0.8;
    Sprite.squash[eid] = -0.4;
    spawnFx(ctx, SpriteId.Shockwave, nx, ny, 0, 2.2, def.glowTint, 0.55);
    emitParticles(ctx.fx, ParticleKind.PortalBurst, nx, ny, 0, 0, 0.9, def.glowTint);
    ctx.events.push(GameEvent.BossBlink, eid, 0, nx, ny);
  }

  // ---------------------------------------------------------------- patterns

  private enraged(eid: number): boolean {
    return BossTag.halfCalled[eid] === 1;
  }

  private restFor(eid: number): number {
    const c = Config.boss;
    const rest = this.tuning.rest * (this.enraged(eid) ? c.enrageRestMult : 1);
    return rest * this.ctx.rng.range(0.85, 1.15);
  }

  private startPattern(eid: number, tx: number, ty: number): void {
    const ctx = this.ctx;
    const def = bossDef(BossTag.kind[eid]);
    const set = this.tuning.patterns;
    let idx = ctx.rng.int(0, set.length - 1);
    if (set.length > 1 && set[idx] === BossTag.pattern[eid]) idx = (idx + 1) % set.length;
    let pattern = set[idx];
    // Summoners only call when the floor has room for more small fry.
    if (pattern === BossPattern.Summon && this.smallFryAlive() >= SUMMON_CAP) pattern = set[(idx + 1) % set.length];
    if (pattern === BossPattern.Summon && this.smallFryAlive() >= SUMMON_CAP) pattern = set[0];
    BossTag.pattern[eid] = pattern;
    BossTag.step[eid] = 0;
    BossTag.stepTime[eid] = 0;
    BossTag.casts[eid]++;
    BossTag.orbCount[eid] = 0;
    const x = Position.x[eid];
    const y = Position.y[eid];
    // Working angle: radial volleys start off-axis so the first ring never lines up
    // with the last one; beams, rings and charges key off the wizard.
    const toPlayer = Math.atan2(ty - y, tx - x);
    BossTag.angle[eid] = pattern === BossPattern.Radial ? ctx.rng.next() * Math.PI * 2 : toPlayer;

    playClip(eid, ClipId.Cast, true, true);
    Anim.speed[eid] = Math.max(0.4, Anims.duration(Sprite.id[eid] as SpriteId, ClipId.Cast) / (SCRIPTS[pattern].telegraph + 0.15));
    Sprite.flash[eid] = 0.55;
    Sprite.squash[eid] = -0.3;
    spawnFx(ctx, SpriteId.Shockwave, x, y, 0, 1.6 + SCRIPTS[pattern].telegraph, def.glowTint, 0.55);
    emitParticles(ctx.fx, ParticleKind.PortalMotes, x, y, 0, 1, 0.8, def.glowTint);
    switch (pattern) {
      case BossPattern.Beam:
        this.telegraphBeam(eid, toPlayer);
        break;
      case BossPattern.Charge:
        this.telegraphCharge(eid, def, toPlayer);
        break;
      case BossPattern.Stomp:
        this.telegraphRing(eid, def, STOMP_RADIUS, SCRIPTS[pattern].telegraph);
        break;
      case BossPattern.Screech:
        this.telegraphRing(eid, def, 4.2, SCRIPTS[pattern].telegraph);
        break;
      default:
        break;
    }
    ctx.events.push(GameEvent.BossTelegraph, eid, pattern, x, y);
  }

  private runPattern(eid: number, tx: number, ty: number, dt: number): void {
    const pattern = BossTag.pattern[eid] as BossPattern;
    const script = SCRIPTS[pattern];
    BossTag.stepTime[eid] += dt;
    const glow = Math.min(1, BossTag.stepTime[eid] / script.telegraph);
    if (BossTag.step[eid] === 0) {
      // Telegraph: glow harder and lean back until the first release.
      Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.25 + glow * 0.45);
      Sprite.squash[eid] = -0.2 - glow * 0.2;
      if (BossTag.stepTime[eid] < script.telegraph) return;
      BossTag.stepTime[eid] = 0;
      BossTag.step[eid] = 1;
      this.release(eid, pattern, 0, tx, ty);
      return;
    }

    switch (pattern) {
      case BossPattern.Orbit:
        this.spinOrbs(eid, ORBIT_SECONDS, 1.6, 1.3);
        if (BossTag.stepTime[eid] < ORBIT_SECONDS) return;
        this.releaseOrbs(eid, tx, ty);
        this.finishPattern(eid);
        return;
      case BossPattern.Crown: {
        // Skulls spin up, then launch one at a time at wherever the wizard is right then.
        const spinning = BossTag.stepTime[eid] < CROWN_SPIN_SECONDS;
        this.spinOrbs(eid, CROWN_SPIN_SECONDS, 1.9, 0.9);
        if (spinning) return;
        BossTag.burstAt[eid] += dt;
        if (BossTag.burstAt[eid] >= CROWN_LAUNCH_GAP) {
          BossTag.burstAt[eid] = 0;
          if (!this.launchNextOrb(eid, tx, ty)) this.finishPattern(eid);
        }
        return;
      }
      case BossPattern.Beam:
        this.sweepBeam(eid, dt);
        if (BossTag.stepTime[eid] >= BEAM_SWEEP_SECONDS) this.finishPattern(eid);
        return;
      case BossPattern.Charge:
        this.runCharge(eid, dt);
        return;
      default:
        break;
    }

    if (BossTag.stepTime[eid] < script.gap) return;
    const volleys = this.volleysFor(eid, pattern);
    const done = BossTag.step[eid]; // releases so far
    if (done >= volleys) {
      this.finishPattern(eid);
      return;
    }
    BossTag.stepTime[eid] = 0;
    BossTag.step[eid] = done + 1;
    this.release(eid, pattern, done, tx, ty);
  }

  private volleysFor(eid: number, pattern: BossPattern): number {
    const bonus = this.enraged(eid) ? 1 : 0;
    const t = this.tuning;
    switch (pattern) {
      case BossPattern.Radial:
        return t.volleys + bonus;
      case BossPattern.Aimed:
        return 3 + (t.tier >= 2 ? 1 : 0) + bonus;
      case BossPattern.RingGap:
        return t.tier >= 2 || this.enraged(eid) ? 2 : 1;
      case BossPattern.Stomp:
        return 2 + bonus;
      case BossPattern.Rubble:
        return 2 + (t.tier >= 1 ? 1 : 0) + bonus;
      case BossPattern.Acid:
        return 2 + (t.tier >= 2 ? 1 : 0) + bonus;
      case BossPattern.Screech:
        return 1 + bonus;
      case BossPattern.Wisps:
      case BossPattern.Summon:
      default:
        return 1;
    }
  }

  private finishPattern(eid: number): void {
    BossTag.rest[eid] = this.restFor(eid);
    BossTag.step[eid] = 0;
    BossTag.stepTime[eid] = 0;
    BossTag.lockX[eid] = 0;
    BossTag.lockY[eid] = 0;
    playClip(eid, ClipId.Idle, true);
    Anim.speed[eid] = 1;
  }

  /** One release of the pattern: spawns the volley and plays the cast beat. */
  private release(eid: number, pattern: BossPattern, index: number, tx: number, ty: number): void {
    const ctx = this.ctx;
    const def = bossDef(BossTag.kind[eid]);
    const x = Position.x[eid];
    const y = Position.y[eid];
    const t = this.tuning;
    const toPlayer = Math.atan2(ty - y, tx - x);
    let heavy = false;

    switch (pattern) {
      case BossPattern.Radial: {
        const count = 10 + t.bonusShots * 2 + (this.enraged(eid) ? 2 : 0);
        // Alternate the ring offset so consecutive rings interleave and the gaps move.
        const base = BossTag.angle[eid] + (index * Math.PI) / count;
        for (let i = 0; i < count; i++) {
          const a = base + (i / count) * Math.PI * 2;
          this.bolt(eid, def, x + Math.cos(a) * 0.9, y + Math.sin(a) * 0.9, a, t.boltSpeed, SpriteId.BossBolt, t.boltDamage, 2.6, 0.24, 1);
        }
        break;
      }
      case BossPattern.Aimed: {
        const shots = Math.min(7, 3 + t.bonusShots + (def.kind === BossKind.Sentinel ? 2 : 0));
        const spread = (def.kind === BossKind.Sentinel ? 34 : 22) * DEG;
        // Lead a little on later floors so strafing in a straight line stops working.
        const aim = toPlayer + (index % 2 === 0 ? 0 : (ctx.rng.next() - 0.5) * 0.18);
        for (let i = 0; i < shots; i++) {
          const a = aim + (shots === 1 ? 0 : (i / (shots - 1) - 0.5) * spread);
          this.bolt(eid, def, x + Math.cos(a) * 1.1, y + Math.sin(a) * 1.1, a, t.boltSpeed * 1.55, SpriteId.BossBolt, t.boltDamage, 2.2, 0.22, 1.05);
        }
        break;
      }
      case BossPattern.Orbit: {
        const count = Math.min(BOSS_ORBS, 6 + t.bonusShots);
        this.spawnOrbs(eid, def, count, SpriteId.BossOrb, 1.6, ORBIT_SECONDS + 4, t.boltDamage + 2);
        break;
      }
      case BossPattern.Crown: {
        const count = Math.min(BOSS_ORBS, 5 + t.tier + Math.floor(t.bonusShots / 2) + (this.enraged(eid) ? 2 : 0));
        this.spawnOrbs(eid, def, count, SpriteId.BossSkull, 1.9, CROWN_SPIN_SECONDS + 6, t.boltDamage + 1);
        BossTag.burstAt[eid] = 0;
        break;
      }
      case BossPattern.RingGap: {
        const count = 26 + t.bonusShots * 2;
        // The hole sits near the wizard's bearing but never exactly on it.
        const gapCenter = toPlayer + (ctx.rng.next() - 0.5) * 70 * DEG + (index === 1 ? Math.PI * 0.5 : 0);
        const gapHalf = (index === 0 ? 24 : 20) * DEG;
        for (let i = 0; i < count; i++) {
          const a = BossTag.angle[eid] + (i / count) * Math.PI * 2;
          let d = a - gapCenter;
          d = Math.atan2(Math.sin(d), Math.cos(d));
          if (Math.abs(d) < gapHalf) continue;
          this.bolt(eid, def, x + Math.cos(a) * 1.0, y + Math.sin(a) * 1.0, a, t.boltSpeed * 0.62, SpriteId.BossOrb, t.boltDamage + 2, 6, 0.3, 1.1);
        }
        break;
      }
      case BossPattern.Wisps: {
        const count = Math.min(6, 3 + Math.floor(t.bonusShots / 2));
        for (let i = 0; i < count; i++) {
          const a = toPlayer + Math.PI + (i / count - 0.5) * Math.PI * 1.2;
          this.bolt(eid, def, x + Math.cos(a) * 1.4, y + Math.sin(a) * 1.4, a, t.boltSpeed * 0.48, SpriteId.BossWisp, t.boltDamage * 0.8, 6.5, 0.3, 1.1);
        }
        break;
      }
      case BossPattern.Beam: {
        // The sweep starts on the telegraphed line (angle was locked at telegraph time).
        BossTag.angle[eid] -= (BEAM_SWEEP_DEG / 2) * DEG * this.beamSide(eid);
        BossTag.burstAt[eid] = 0;
        break;
      }
      case BossPattern.Stomp: {
        heavy = true;
        this.ring(eid, def, x, y, STOMP_RADIUS / STOMP_SECONDS, STOMP_SECONDS, t.boltDamage, 15, def.glowTint);
        // Rubble rains outward from the impact; later stomps throw more.
        const chunks = 5 + t.tier * 2 + index * 2;
        for (let i = 0; i < chunks; i++) {
          const a = ctx.rng.next() * Math.PI * 2;
          this.bolt(eid, def, x + Math.cos(a) * 1.3, y + Math.sin(a) * 1.3, a, t.boltSpeed * (0.45 + ctx.rng.next() * 0.5), SpriteId.BossRubble, t.boltDamage * 0.8, 1.6, 0.28, 1.1);
        }
        requestDecal(ctx.fx, x, y, def.stainTint, 1.6);
        emitParticles(ctx.fx, ParticleKind.GateDust, x, y - 0.8, 0, 1, 1.6);
        ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 70);
        ctx.fx.shakeY += 1.1;
        ctx.fx.shakeX += 0.4;
        ctx.fx.rumble = 1;
        ctx.events.push(GameEvent.BossImpact, BossImpactKind.Stomp, def.kind, x, y);
        break;
      }
      case BossPattern.Charge: {
        // Lock the line at release: the telegraph already showed it.
        const a = BossTag.angle[eid];
        BossTag.lockX[eid] = Math.cos(a);
        BossTag.lockY[eid] = Math.sin(a);
        // Flyers dive (a short, fast swoop); walkers charge the whole lane.
        const dives = def.motion === BossMotion.Flyer;
        const speed = (dives ? DIVE_SPEED : CHARGE_SPEED) * (this.enraged(eid) ? 1.15 : 1);
        Velocity.x[eid] = BossTag.lockX[eid] * speed;
        Velocity.y[eid] = BossTag.lockY[eid] * speed;
        Sprite.face[eid] = BossTag.lockX[eid] < 0 ? -1 : 1;
        Sprite.squash[eid] = -0.35;
        if (dives) BossTag.lift[eid] = 0.2;
        heavy = true;
        break;
      }
      case BossPattern.Rubble: {
        const shots = Math.min(8, 4 + t.bonusShots + (this.enraged(eid) ? 1 : 0));
        const spread = 42 * DEG;
        for (let i = 0; i < shots; i++) {
          const a = toPlayer + (i / (shots - 1) - 0.5) * spread + (ctx.rng.next() - 0.5) * 0.08;
          const sp = t.boltSpeed * (0.85 + ctx.rng.next() * 0.3);
          this.bolt(eid, def, x + Math.cos(a) * 1.4, y + Math.sin(a) * 1.4, a, sp, SpriteId.BossRubble, t.boltDamage, 2.4, 0.3, 1.15 + ctx.rng.next() * 0.3);
        }
        heavy = true;
        break;
      }
      case BossPattern.Summon: {
        const count = 3 + t.tier + (this.enraged(eid) ? 1 : 0);
        this.summon(eid, def, count);
        break;
      }
      case BossPattern.Acid: {
        const globs = Math.min(7, 3 + t.tier + Math.floor(t.bonusShots / 2));
        const spread = 50 * DEG;
        for (let i = 0; i < globs; i++) {
          const a = toPlayer + (globs === 1 ? 0 : (i / (globs - 1) - 0.5) * spread) + (ctx.rng.next() - 0.5) * 0.1;
          // Flight time is the arc: the glob bursts into a splash where it lands.
          const range = 0.6 + ctx.rng.next() * 0.55;
          this.bolt(eid, def, x + Math.cos(a) * 1.4, y + Math.sin(a) * 1.4, a, t.boltSpeed * 0.8, SpriteId.BossAcid, t.boltDamage * 0.9, range, 0.34, 1.2);
        }
        break;
      }
      case BossPattern.Screech: {
        this.ring(eid, def, x, y, 4.2 / 0.45, 0.45, t.boltDamage * 0.5, 24, def.glowTint);
        emitParticles(ctx.fx, ParticleKind.PortalBurst, x, y, 0, 0, 1.2, def.glowTint);
        ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 50);
        ctx.fx.shakeX += 0.8;
        ctx.fx.shakeY += 0.5;
        ctx.events.push(GameEvent.BossImpact, BossImpactKind.Screech, def.kind, x, y);
        heavy = true;
        break;
      }
      default:
        break;
    }

    playClip(eid, ClipId.Cast, true, true);
    Anim.speed[eid] = 1.3;
    Sprite.squash[eid] = heavy ? 0.5 : 0.4;
    Sprite.flash[eid] = 0.9;
    spawnFx(ctx, SpriteId.MuzzleWand, x + Math.cos(toPlayer) * 1.2, y + Math.sin(toPlayer) * 1.2, toPlayer, 1.8, def.boltTint, 0.82);
    emitParticles(ctx.fx, ParticleKind.MuzzleSparks, x, y, Math.cos(toPlayer), Math.sin(toPlayer), 1.2, def.boltTint);
    ctx.fx.shakeX += Math.cos(toPlayer) * 0.12;
    ctx.fx.shakeY += Math.sin(toPlayer) * 0.12;
    ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.45);
    ctx.events.push(GameEvent.BossCast, eid, pattern, x, y);
  }

  /** Beam sweeps toward the side the wizard is moving to, alternating per cast otherwise. */
  private beamSide(eid: number): number {
    return BossTag.casts[eid] % 2 === 0 ? 1 : -1;
  }

  private bolt(
    owner: number,
    def: BossDef,
    x: number,
    y: number,
    angle: number,
    speed: number,
    sprite: SpriteId,
    damage: number,
    life: number,
    radius: number,
    scale: number,
  ): number {
    const s = this.shot;
    s.owner = owner;
    s.sprite = sprite;
    s.x = x;
    s.y = y;
    s.vx = Math.cos(angle) * speed;
    s.vy = Math.sin(angle) * speed;
    s.radius = radius;
    s.life = life;
    s.damage = damage;
    s.scale = scale;
    s.grow = 0;
    s.pierce = 0;
    switch (sprite) {
      case SpriteId.BossBolt:
        s.tint = def.boltTint;
        s.knockback = 4;
        break;
      case SpriteId.BossOrb:
      case SpriteId.BossSkull:
        s.tint = def.orbTint;
        s.knockback = 6;
        break;
      case SpriteId.BossRubble:
        s.tint = def.orbTint;
        s.knockback = 7;
        break;
      case SpriteId.BossAcid:
        s.tint = def.boltTint;
        s.knockback = 3;
        break;
      default:
        s.tint = def.wispTint;
        s.knockback = 4;
        break;
    }
    const eid = spawnProjectile(this.ctx, s);
    if (eid !== NO_ENTITY) {
      if (speed === 0) Sprite.rot[eid] = angle;
      // Rubble tumbles and drags to a halt; acid globs swell as they arc.
      if (sprite === SpriteId.BossRubble) Sprite.rot[eid] = this.ctx.rng.next() * Math.PI * 2;
    }
    return eid;
  }

  /** Stationary expanding ring (stomp / screech): hits each wizard once and shoves them outward. */
  private ring(owner: number, def: BossDef, x: number, y: number, grow: number, life: number, damage: number, knockback: number, tint: number): void {
    const s = this.shot;
    s.owner = owner;
    s.sprite = SpriteId.Shockwave;
    s.x = x;
    s.y = y;
    s.vx = 0;
    s.vy = 0;
    s.radius = 0.6;
    s.life = life;
    s.damage = damage;
    s.scale = 1.2;
    s.grow = grow;
    s.pierce = 8;
    s.tint = tint;
    s.knockback = knockback;
    spawnProjectile(this.ctx, s);
    void def;
  }

  // ---------------------------------------------------------- telegraph art

  /** Dotted rune ring at the reach of a stomp / screech so the safe distance reads. */
  private telegraphRing(eid: number, def: BossDef, radius: number, life: number): void {
    const ctx = this.ctx;
    const x = Position.x[eid];
    const y = Position.y[eid];
    const n = 20;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const px = x + Math.cos(a) * radius;
      const py = y + Math.sin(a) * radius;
      if (px < BOUNDS.minX || px > BOUNDS.maxX || py < BOUNDS.minY || py > BOUNDS.maxY) continue;
      const fx = spawnFx(ctx, SpriteId.BossBolt, px, py, a + Math.PI / 2, 0.75, def.glowTint, 0.5);
      if (fx === NO_ENTITY) return;
      Lifetime.t[fx] = life;
      Sprite.alpha[fx] = 0.45;
    }
  }

  /** Dotted line along the charge so the wizard knows which lane to leave. */
  private telegraphCharge(eid: number, def: BossDef, angle: number): void {
    const ctx = this.ctx;
    const x = Position.x[eid];
    const y = Position.y[eid];
    const life = SCRIPTS[BossPattern.Charge].telegraph;
    const reach = def.motion === BossMotion.Flyer ? DIVE_SPEED * DIVE_SECONDS : 40;
    for (let i = 0; i < 24; i++) {
      const d = 1.6 + i * 1.3;
      if (d > reach) break;
      const px = x + Math.cos(angle) * d;
      const py = y + Math.sin(angle) * d;
      if (px < BOUNDS.minX || px > BOUNDS.maxX || py < BOUNDS.minY || py > BOUNDS.maxY) break;
      const fx = spawnFx(ctx, SpriteId.BossBolt, px, py, angle, 0.9, def.glowTint, 0.5);
      if (fx === NO_ENTITY) return;
      Lifetime.t[fx] = life;
      Sprite.alpha[fx] = 0.5;
    }
  }

  // ----------------------------------------------------------------- charge

  private runCharge(eid: number, dt: number): void {
    const ctx = this.ctx;
    const def = bossDef(BossTag.kind[eid]);
    const dive = def.motion === BossMotion.Flyer;
    const speed = (dive ? DIVE_SPEED : CHARGE_SPEED) * (this.enraged(eid) ? 1.15 : 1);
    Velocity.x[eid] = BossTag.lockX[eid] * speed;
    Velocity.y[eid] = BossTag.lockY[eid] * speed;
    const x = Position.x[eid];
    const y = Position.y[eid];
    if (ctx.rng.chance(dt * 30)) {
      emitParticles(ctx.fx, dive ? ParticleKind.PortalMotes : ParticleKind.GateDust, x, y - 0.6, -BossTag.lockX[eid], -BossTag.lockY[eid], 0.6, dive ? def.glowTint : 0xffffff);
    }
    ctx.fx.rumble = Math.max(ctx.fx.rumble, dive ? 0.5 : 0.8);
    if (!dive) ctx.fx.shakeY += dt * 1.5;

    const r = Radius.r[eid] + 0.25;
    const nx = x + BossTag.lockX[eid] * r;
    const ny = y + BossTag.lockY[eid] * r;
    const wall = nx < BOUNDS.minX || nx > BOUNDS.maxX || ny < BOUNDS.minY || ny > BOUNDS.maxY;
    const timeUp = dive && BossTag.stepTime[eid] >= DIVE_SECONDS;
    if (!wall && !timeUp) return;

    // Impact: the wall gets it (Warlord) or the dive pulls up (Matriarch).
    Velocity.x[eid] = 0;
    Velocity.y[eid] = 0;
    if (wall && !dive) {
      spawnFx(ctx, SpriteId.Shockwave, x, y, 0, 3.6, def.glowTint, 0.55);
      emitParticles(ctx.fx, ParticleKind.GateDust, nx, ny, -BossTag.lockX[eid], -BossTag.lockY[eid], 1.6);
      emitParticles(ctx.fx, ParticleKind.HitSpark, nx, ny, -BossTag.lockX[eid], -BossTag.lockY[eid], 1.2, def.glowTint);
      // Chips fly back into the room off the wall.
      for (let i = 0; i < 6; i++) {
        const a = Math.atan2(-BossTag.lockY[eid], -BossTag.lockX[eid]) + (ctx.rng.next() - 0.5) * 1.6;
        this.bolt(eid, def, x, y, a, this.tuning.boltSpeed * (0.5 + ctx.rng.next() * 0.4), SpriteId.BossRubble, this.tuning.boltDamage * 0.7, 1.5, 0.26, 1);
      }
      Sprite.squash[eid] = 0.55;
      Sprite.joltX[eid] = -BossTag.lockX[eid] * 0.5;
      ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 90);
      ctx.fx.shakeX += BossTag.lockX[eid] * 1.4;
      ctx.fx.shakeY += BossTag.lockY[eid] * 1.4 + 0.5;
      ctx.fx.rumble = 1;
      ctx.events.push(GameEvent.BossImpact, BossImpactKind.Wall, def.kind, x, y);
    } else {
      Sprite.squash[eid] = -0.3;
      BossTag.lift[eid] = def.lift;
    }
    this.finishPattern(eid);
    // The Warlord is dazed after hitting stone: a longer opening.
    if (wall && !dive) BossTag.rest[eid] = Math.max(BossTag.rest[eid], 1.5);
  }

  // ---------------------------------------------------------------- summons

  private smallFryAlive(): number {
    const enemies = query(this.ctx.world, [Enemy]);
    let n = 0;
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (Enemy.kind[e] !== EnemyKind.Boss && !isWallEnemy(Enemy.kind[e]) && !Dying.flag[e]) n++;
    }
    return n;
  }

  private spawnMinion(kind: EnemyKind, x: number, y: number): number {
    const ctx = this.ctx;
    const cx = Math.max(BOUNDS.minX + 0.6, Math.min(BOUNDS.maxX - 0.6, x));
    const cy = Math.max(BOUNDS.minY + 0.6, Math.min(BOUNDS.maxY - 0.6, y));
    switch (kind) {
      case EnemyKind.Skull:
        return spawnSkull(ctx, cx, cy);
      case EnemyKind.Bat:
        return spawnBat(ctx, cx, cy);
      case EnemyKind.Slime:
        return spawnSlime(ctx, cx, cy);
      case EnemyKind.Golem:
        return spawnGolem(ctx, cx, cy);
      case EnemyKind.Hexer:
        return spawnHexer(ctx, cx, cy);
      case EnemyKind.Cinder:
        return spawnCinder(ctx, cx, cy);
      case EnemyKind.Wraith:
        return spawnWraith(ctx, cx, cy);
      default:
        return NO_ENTITY;
    }
  }

  /** Calls small fry in a ring around the boss (Skull King's court, the Matriarch's brood). */
  private summon(eid: number, def: BossDef, count: number): void {
    if (def.minion === -1) return;
    const ctx = this.ctx;
    const x = Position.x[eid];
    const y = Position.y[eid];
    const room = Math.max(0, SUMMON_CAP - this.smallFryAlive());
    const n = Math.min(count, room);
    let spawned = 0;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + ctx.rng.next() * 0.5;
      const r = Radius.r[eid] + 1.4;
      const mx = x + Math.cos(a) * r;
      const my = y + Math.sin(a) * r;
      const m = this.spawnMinion(def.minion, mx, my);
      if (m === NO_ENTITY) break;
      spawned++;
      Sprite.pop[m] = 0.8;
      Sprite.flash[m] = 0.9;
      Health.invuln[m] = 0.3;
      spawnFx(ctx, SpriteId.Smoke, mx, my, ctx.rng.next() * Math.PI * 2, 1.4, def.glowTint, 0.5);
      emitParticles(ctx.fx, ParticleKind.PortalMotes, mx, my, 0, 1, 0.8, def.glowTint);
    }
    if (spawned > 0) ctx.events.push(GameEvent.BossSummon, eid, spawned, x, y);
  }

  /** Colossus: at 50% and 25% it sheds slimes and gets smaller (and faster). */
  private maybeSplit(eid: number, def: BossDef): void {
    const frac = Health.hp[eid] / Math.max(1, Health.max[eid]);
    const stage = BossTag.splits[eid];
    if (!((stage === 0 && frac <= 0.5) || (stage === 1 && frac <= 0.25))) return;
    BossTag.splits[eid] = stage + 1;
    const ctx = this.ctx;
    const x = Position.x[eid];
    const y = Position.y[eid];
    Sprite.scale[eid] *= 0.84;
    Radius.r[eid] *= 0.88;
    Sprite.squash[eid] = 0.6;
    Sprite.pop[eid] = 0.5;
    Sprite.flash[eid] = 1;
    emitParticles(ctx.fx, ParticleKind.ImpGoo, x, y, 0, 1, 2.2, def.orbTint);
    spawnFx(ctx, SpriteId.Shockwave, x, y, 0, 3.4, def.glowTint, 0.55);
    requestDecal(ctx.fx, x, y, def.stainTint, 2.4);
    ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 90);
    ctx.fx.shakeY += 0.8;
    ctx.events.push(GameEvent.BossImpact, BossImpactKind.Split, def.kind, x, y);
    this.summon(eid, def, stage === 0 ? 4 : 3);
    // Interrupt whatever it was doing; the split is the beat.
    BossTag.rest[eid] = Math.max(BossTag.rest[eid], 0.9);
    BossTag.step[eid] = 0;
    BossTag.stepTime[eid] = 0;
    BossTag.orbCount[eid] = 0;
  }

  // ------------------------------------------------------------- orbit orbs

  private orbAlive(boss: number, orb: number): boolean {
    if (orb === -1 || Dying.flag[orb] || Projectile.owner[orb] !== boss || TeamTag.team[orb] !== Team.Enemy) return false;
    const id = Sprite.id[orb];
    return id === SpriteId.BossOrb || id === SpriteId.BossSkull;
  }

  private spawnOrbs(eid: number, def: BossDef, count: number, sprite: SpriteId, radius: number, life: number, damage: number): void {
    const x = Position.x[eid];
    const y = Position.y[eid];
    BossTag.orbCount[eid] = 0;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2;
      const orb = this.bolt(eid, def, x + Math.cos(a) * radius, y + Math.sin(a) * radius, a, 0, sprite, damage, life, 0.3, 1.15);
      if (orb === NO_ENTITY) break;
      BossTag.orbs[eid * BOSS_ORBS + BossTag.orbCount[eid]++] = orb;
    }
  }

  private spinOrbs(eid: number, seconds: number, baseRadius: number, widen: number): void {
    const n = BossTag.orbCount[eid];
    if (n === 0) return;
    const t = BossTag.stepTime[eid];
    const u = Math.min(1, t / seconds);
    // Widen the ring as it spins up so the orbs read as gathering power.
    const r = baseRadius + u * widen;
    const spin = t * (3.4 + u * 2.2);
    const x = Position.x[eid];
    const y = Position.y[eid];
    const lift = BossTag.lift[eid];
    for (let i = 0; i < n; i++) {
      const orb = BossTag.orbs[eid * BOSS_ORBS + i];
      if (!this.orbAlive(eid, orb)) continue;
      // Launched orbs (crown) keep flying; only parked ones ride the ring.
      if (Math.abs(Velocity.x[orb]) + Math.abs(Velocity.y[orb]) > 0.01) continue;
      const a = spin + (i / n) * Math.PI * 2;
      Position.x[orb] = x + Math.cos(a) * r;
      Position.y[orb] = y + Math.sin(a) * r * 0.75 + lift;
      Velocity.x[orb] = 0;
      Velocity.y[orb] = 0;
      Sprite.rot[orb] = a + Math.PI / 2;
      Sprite.scale[orb] = 1.15 + u * 0.35;
      Lifetime.t[orb] = seconds + 4;
    }
    if (this.ctx.rng.chance(0.3)) {
      const def = bossDef(BossTag.kind[eid]);
      emitParticles(this.ctx.fx, ParticleKind.BoltTrail, x + Math.cos(spin) * r, y + Math.sin(spin) * r, 0, 0, 0.5, def.orbTint);
    }
  }

  private releaseOrbs(eid: number, tx: number, ty: number): void {
    const ctx = this.ctx;
    const def = bossDef(BossTag.kind[eid]);
    const n = BossTag.orbCount[eid];
    const speed = this.tuning.boltSpeed * 1.25;
    for (let i = 0; i < n; i++) {
      const orb = BossTag.orbs[eid * BOSS_ORBS + i];
      if (!this.orbAlive(eid, orb)) continue;
      const ox = Position.x[orb];
      const oy = Position.y[orb];
      // Fan slightly around the wizard so a single sidestep does not dodge all of them.
      const a = Math.atan2(ty - oy, tx - ox) + (ctx.rng.next() - 0.5) * 0.3;
      Velocity.x[orb] = Math.cos(a) * speed;
      Velocity.y[orb] = Math.sin(a) * speed;
      Lifetime.t[orb] = 3.5;
      emitParticles(ctx.fx, ParticleKind.HitSpark, ox, oy, Math.cos(a), Math.sin(a), 0.5, def.orbTint);
    }
    BossTag.orbCount[eid] = 0;
    Sprite.squash[eid] = 0.5;
    Sprite.flash[eid] = 0.9;
    ctx.fx.shakeX += 0.25;
    ctx.events.push(GameEvent.BossCast, eid, BossPattern.Orbit, Position.x[eid], Position.y[eid]);
  }

  /** Crown: fires the next parked skull at the wizard. Returns false when none are left. */
  private launchNextOrb(eid: number, tx: number, ty: number): boolean {
    const ctx = this.ctx;
    const def = bossDef(BossTag.kind[eid]);
    const n = BossTag.orbCount[eid];
    const speed = this.tuning.boltSpeed * 1.45;
    for (let i = 0; i < n; i++) {
      const orb = BossTag.orbs[eid * BOSS_ORBS + i];
      if (!this.orbAlive(eid, orb)) continue;
      if (Math.abs(Velocity.x[orb]) + Math.abs(Velocity.y[orb]) > 0.01) continue;
      const ox = Position.x[orb];
      const oy = Position.y[orb];
      const a = Math.atan2(ty - oy, tx - ox) + (ctx.rng.next() - 0.5) * 0.12;
      Velocity.x[orb] = Math.cos(a) * speed;
      Velocity.y[orb] = Math.sin(a) * speed;
      Sprite.rot[orb] = a;
      Lifetime.t[orb] = 3;
      emitParticles(ctx.fx, ParticleKind.HitSpark, ox, oy, Math.cos(a), Math.sin(a), 0.5, def.orbTint);
      Sprite.squash[eid] = 0.3;
      Sprite.flash[eid] = 0.7;
      ctx.events.push(GameEvent.BossCast, eid, BossPattern.Crown, ox, oy);
      return true;
    }
    BossTag.orbCount[eid] = 0;
    return false;
  }

  // ------------------------------------------------------------------- beam

  /** Dotted rune lines show where the sweep starts and where it ends. */
  private telegraphBeam(eid: number, toPlayer: number): void {
    const ctx = this.ctx;
    const def = bossDef(BossTag.kind[eid]);
    const side = this.beamSide(eid);
    const start = toPlayer - (BEAM_SWEEP_DEG / 2) * DEG * side;
    const end = toPlayer + (BEAM_SWEEP_DEG / 2) * DEG * side;
    const life = SCRIPTS[BossPattern.Beam].telegraph;
    const x = Position.x[eid];
    const y = Position.y[eid];
    for (let pass = 0; pass < 2; pass++) {
      const a = pass === 0 ? start : end;
      const alpha = pass === 0 ? 0.5 : 0.22;
      for (let i = 0; i < 12; i++) {
        const d = 1.8 + i * 1.55;
        const px = x + Math.cos(a) * d;
        const py = y + Math.sin(a) * d;
        if (px < BOUNDS.minX || px > BOUNDS.maxX || py < BOUNDS.minY || py > BOUNDS.maxY) break;
        const fx = spawnFx(ctx, SpriteId.BossBolt, px, py, a, 0.8, def.glowTint, 0.5);
        if (fx === NO_ENTITY) return;
        Lifetime.t[fx] = life;
        Sprite.alpha[fx] = alpha;
      }
    }
  }

  private sweepBeam(eid: number, dt: number): void {
    const ctx = this.ctx;
    const def = bossDef(BossTag.kind[eid]);
    const t = this.tuning;
    const side = this.beamSide(eid);
    const rate = (BEAM_SWEEP_DEG * DEG) / BEAM_SWEEP_SECONDS;
    BossTag.angle[eid] += rate * side * dt;
    const a = BossTag.angle[eid];
    const x = Position.x[eid];
    const y = Position.y[eid];
    // Shots stream out fast enough to read as a solid rotating beam.
    BossTag.burstAt[eid] += dt;
    while (BossTag.burstAt[eid] >= BEAM_SHOT_GAP) {
      BossTag.burstAt[eid] -= BEAM_SHOT_GAP;
      this.bolt(eid, def, x + Math.cos(a) * 1.2, y + Math.sin(a) * 1.2, a, t.boltSpeed * 2.3, SpriteId.BossBolt, t.boltDamage * 0.7, 1.8, 0.2, 0.9);
    }
    Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.5);
    Sprite.face[eid] = Math.cos(a) < 0 ? -1 : 1;
    if (ctx.rng.chance(dt * 20)) emitParticles(ctx.fx, ParticleKind.MuzzleSparks, x + Math.cos(a) * 1.3, y + Math.sin(a) * 1.3, Math.cos(a), Math.sin(a), 0.6, def.boltTint);
  }

  // ------------------------------------------------------------------ wisps

  private steerWisps(players: ArrayLike<number>, dt: number): void {
    const ctx = this.ctx;
    const shots = query(ctx.world, [Projectile, Position, Velocity, Sprite, TeamTag]);
    const maxTurn = WISP_TURN_RATE * dt;
    for (let i = 0; i < shots.length; i++) {
      const eid = shots[i];
      if (TeamTag.team[eid] !== Team.Enemy || Dying.flag[eid] || FxTag.flag[eid]) continue;
      const id = Sprite.id[eid];
      if (id === SpriteId.BossRubble) {
        // Rubble tumbles and skids: it loses speed and spins as it goes.
        const drag = Math.exp(-1.1 * dt);
        Velocity.x[eid] *= drag;
        Velocity.y[eid] *= drag;
        Sprite.rot[eid] += dt * 6;
        continue;
      }
      if (id !== SpriteId.BossWisp) continue;
      const vx = Velocity.x[eid];
      const vy = Velocity.y[eid];
      const speed = Math.hypot(vx, vy);
      if (speed < 0.01) continue;
      let tx = 0;
      let ty = 0;
      let best = Infinity;
      for (let p = 0; p < players.length; p++) {
        const pid = players[p];
        if (Player.dead[pid]) continue;
        const dx = Position.x[pid] - Position.x[eid];
        const dy = Position.y[pid] - Position.y[eid];
        const d2 = dx * dx + dy * dy;
        if (d2 >= best) continue;
        best = d2;
        tx = dx;
        ty = dy;
      }
      if (best === Infinity) continue;
      const heading = Math.atan2(vy, vx);
      const want = Math.atan2(ty, tx) + Math.sin(ctx.time * 5 + eid) * 0.25;
      let diff = want - heading;
      diff = Math.atan2(Math.sin(diff), Math.cos(diff));
      const next = heading + Math.max(-maxTurn, Math.min(maxTurn, diff));
      Velocity.x[eid] = Math.cos(next) * speed;
      Velocity.y[eid] = Math.sin(next) * speed;
      // Wisps bob and pulse so they read as alive, not as slow bullets.
      Sprite.scale[eid] = 1.05 + Math.sin(ctx.time * 9 + eid) * 0.12;
      if (ctx.rng.chance(dt * 12)) emitParticles(ctx.fx, ParticleKind.BoltTrail, Position.x[eid], Position.y[eid], -vx, -vy, 0.5, Sprite.tint[eid]);
    }
  }

  // ------------------------------------------------------------------- acid

  /** Acid globs burst where they land: a puddle decal and a slow splash of droplets. */
  private burstAcid(dt: number): void {
    const ctx = this.ctx;
    const shots = query(ctx.world, [Projectile, Position, Lifetime, Sprite, TeamTag]);
    for (let i = 0; i < shots.length; i++) {
      const eid = shots[i];
      if (Sprite.id[eid] !== SpriteId.BossAcid || TeamTag.team[eid] !== Team.Enemy || Dying.flag[eid] || FxTag.flag[eid]) continue;
      // Swell along the arc.
      Sprite.scale[eid] = 1.2 + Math.sin(ctx.time * 14 + eid) * 0.08;
      if (Lifetime.t[eid] > dt) continue;
      const x = Position.x[eid];
      const y = Position.y[eid];
      const owner = Projectile.owner[eid];
      const def = bossDef(owner >= 0 ? BossTag.kind[owner] : BossKind.Colossus);
      const tint = Sprite.tint[eid];
      requestDecal(ctx.fx, x, y, def.stainTint, 1.1);
      emitParticles(ctx.fx, ParticleKind.ImpGoo, x, y, 0, 1, 0.9, tint);
      spawnFx(ctx, SpriteId.Shockwave, x, y, 0, 1.4, tint, 0.5);
      const n = 6;
      const base = ctx.rng.next() * Math.PI * 2;
      for (let k = 0; k < n; k++) {
        const a = base + (k / n) * Math.PI * 2;
        this.bolt(owner, def, x + Math.cos(a) * 0.3, y + Math.sin(a) * 0.3, a, this.tuning.boltSpeed * 0.42, SpriteId.BossOrb, Math.max(3, Damage.amount[eid] * 0.6), 1.3, 0.2, 0.75);
      }
      ctx.events.push(GameEvent.BossImpact, BossImpactKind.Splat, BossKind.Colossus, x, y);
      kill(eid);
    }
  }

  // ------------------------------------------------------------------ death

  /** Timed bursts across the death clip, then one big flash as the body goes. */
  private crescendo(eid: number, dt: number): void {
    const ctx = this.ctx;
    const def = bossDef(BossTag.kind[eid]);
    const T = this.tuning.deathSeconds;
    BossTag.phaseTime[eid] += dt;
    const t = BossTag.phaseTime[eid];
    const u = Math.min(1, t / T);
    const x = Position.x[eid];
    const y = Position.y[eid];
    Velocity.x[eid] *= 0.8;
    Velocity.y[eid] *= 0.8;
    // Grounded bosses sag; flyers lose height as they go.
    if (def.lift > 0) BossTag.lift[eid] = def.lift * (1 - u * 0.8);
    // Shudder harder as it goes.
    Sprite.joltX[eid] = (ctx.rng.next() - 0.5) * 0.25 * u;
    Sprite.joltY[eid] = (ctx.rng.next() - 0.5) * 0.25 * u;
    Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.3 + u * 0.5);
    ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.4 + u * 0.6);

    if (t - BossTag.burstAt[eid] >= CRESCENDO_BURST_GAP * (1.2 - u * 0.6)) {
      BossTag.burstAt[eid] = t;
      const a = ctx.rng.next() * Math.PI * 2;
      const r = ctx.rng.next() * Radius.r[eid] * 1.3;
      const bx = x + Math.cos(a) * r;
      const by = y + Math.sin(a) * r + 0.6 + BossTag.lift[eid];
      spawnFx(ctx, SpriteId.Explosion, bx, by, ctx.rng.next() * Math.PI * 2, 1.4 + u * 1.6, ctx.rng.chance(0.5) ? def.glowTint : 0xffffff, 0.86);
      emitParticles(ctx.fx, ParticleKind.Embers, bx, by, Math.cos(a), Math.sin(a), 0.8 + u, def.boltTint);
      emitParticles(ctx.fx, ParticleKind.HitSpark, bx, by, 0, 1, 0.6, def.orbTint);
      if (def.body === BossBody.Goo) emitParticles(ctx.fx, ParticleKind.ImpGoo, bx, by, Math.cos(a), Math.sin(a), 0.7, def.orbTint);
      emitBossKillGore(ctx, def.body, bx, by, 0.32);
      ctx.fx.shakeX += (ctx.rng.next() - 0.5) * (0.3 + u * 0.6);
      ctx.fx.shakeY += (ctx.rng.next() - 0.5) * (0.3 + u * 0.6);
    }

    // Final beat: everything left goes up at once and the rune circle shatters.
    const finalAt = T - 0.18;
    if (t >= finalAt && t - dt < finalAt) {
      spawnFx(ctx, SpriteId.Explosion, x, y + 0.5, ctx.rng.next() * Math.PI * 2, 5.5, 0xffffff, 0.9);
      spawnFx(ctx, SpriteId.Shockwave, x, y, 0, 6, def.glowTint, 0.88);
      spawnFx(ctx, SpriteId.Shockwave, x, y, 0, 9, 0xffffff, 0.86);
      emitParticles(ctx.fx, ParticleKind.PortalBurst, x, y, 0, 0, 3, def.glowTint);
      emitParticles(ctx.fx, ParticleKind.Confetti, x, y + 1, 0, 1, 1.6);
      emitBossKillGore(ctx, def.body, x, y, 2);
      // Rune shards fly out along the circle.
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        const fx = spawnFx(ctx, SpriteId.BossBolt, x + Math.cos(a) * 2.4, y + Math.sin(a) * 2.4, a, 0.9, def.glowTint, 0.6);
        if (fx !== NO_ENTITY) Lifetime.t[fx] = 0.5;
      }
      requestDecal(ctx.fx, x, y, def.stainTint, 3.4);
      ctx.fx.flash = Math.max(ctx.fx.flash, 0.95);
      ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 180);
      ctx.fx.shakeX += 1;
      ctx.fx.shakeY += 1.2;
      ctx.fx.rumble = 1;
      this.wipeSmallFry(def);
      this.dropLoot(x, y, def);
      ctx.events.push(GameEvent.BossDown, eid, def.kind, x, y);
    }
    if (ctx.room.phase !== RoomPhase.Fight && ctx.room.phase !== RoomPhase.Clear) kill(eid);
  }

  /** The death blast takes every summoned straggler with it so the gates can open. */
  private wipeSmallFry(def: BossDef): void {
    const ctx = this.ctx;
    const enemies = query(ctx.world, [Enemy, Position]);
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (Enemy.kind[e] === EnemyKind.Boss || Dying.flag[e]) continue;
      emitParticles(ctx.fx, ParticleKind.HitSpark, Position.x[e], Position.y[e], 0, 1, 0.8, def.glowTint);
      spawnFx(ctx, SpriteId.Smoke, Position.x[e], Position.y[e], ctx.rng.next() * Math.PI * 2, 1.2, def.glowTint, 0.5);
      kill(e);
    }
  }

  /** The prize for the duel: a weapon, hearts to patch up, and a fountain of gems. */
  private dropLoot(x: number, y: number, def: BossDef): void {
    const ctx = this.ctx;
    const weapon = DROP_POOL.length > 0 ? DROP_POOL[ctx.rng.int(0, DROP_POOL.length - 1)] : WeaponId.Wand;
    const w = spawnWeaponPickup(ctx, x, y - 0.4, weapon);
    if (w !== NO_ENTITY) Velocity.y[w] = -3;
    const hearts = def.archmage ? 2 : 1 + (ctx.rng.chance(0.5) ? 1 : 0);
    for (let h = 0; h < hearts; h++) {
      const e = spawnHeart(ctx, x, y + 0.4);
      if (e === NO_ENTITY) break;
      const a = Math.PI * (0.25 + 0.5 * (hearts === 1 ? 0.5 : h / (hearts - 1)));
      Velocity.x[e] = Math.cos(a) * 7;
      Velocity.y[e] = Math.sin(a) * 7;
    }
    const gems = def.archmage ? 12 : 8;
    for (let g = 0; g < gems; g++) {
      const e = spawnGem(ctx, x, y, 3);
      if (e === NO_ENTITY) break;
      // Fountain: gems arc up and out and MovementSystem's pickup drag settles them.
      const a = ctx.rng.range(0.15, 0.85) * Math.PI;
      const sp = ctx.rng.range(5, 10);
      Velocity.x[e] = Math.cos(a) * sp;
      Velocity.y[e] = Math.sin(a) * sp;
    }
    if (def.archmage) {
      const p = spawnPrize(ctx, x, y + 0.6, randomPrizeKind(ctx.rng));
      if (p !== NO_ENTITY) Velocity.y[p] = 6;
    }
  }
}
